import { Outlet } from 'react-router';
import type { AdminRole } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { NotAuthorizedPage } from '@/pages/NotAuthorizedPage';

interface Props {
  /** Exact-role constraint — e.g. requiredRole="root" for Admin Users. */
  requiredRole?: AdminRole;
  /** Allowlist constraint — e.g. the console tier for Customers + Network. */
  allowedRoles?: AdminRole[];
}

/**
 * Route guard that waits for the `staffMe` query to settle, then:
 *   - renders `<PendingApprovalPage />` if adminUser is null (no row / disabled)
 *   - renders `<NotAuthorizedPage />` if a role constraint is set and the
 *     caller's role doesn't satisfy it (e.g. a `member` hitting a console route)
 *   - renders `<Outlet />` otherwise
 *
 * The backend `AdminRoleGuard` is the real enforcement point — this is UX so
 * signed-in users see something intelligible instead of a blank page / raw 403.
 */
export function AdminRoleRoute({ requiredRole, allowedRoles }: Props) {
  const adminUser = useAuthStore((s) => s.adminUser);
  const adminUserLoaded = useAuthStore((s) => s.adminUserLoaded);

  if (!adminUserLoaded) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!adminUser) {
    return <PendingApprovalPage />;
  }

  if (requiredRole && !adminUser.roles?.includes(requiredRole)) {
    return <NotAuthorizedPage />;
  }

  if (allowedRoles && !adminUser.roles?.some((r) => allowedRoles.includes(r))) {
    return <NotAuthorizedPage />;
  }

  return <Outlet />;
}
